import React, { createContext, FC, ReactNode, useMemo, useState } from "react";
import { useApiController } from "@app/pippa/hooks";
import {
  CreateNewUserProps,
  ChatActionResponse,
  ChatHistoryResponse,
  SubmitMessageFeedbackProps,
} from "@app/pippa/hooks/useApiController";
import { StreamMessageCallbacks } from "@app/pippa/models";
import { ChatActionTypes } from "@app/pippa/models/constants";

interface APIController {
  apiProviderState: APIProviderState;
  getUserChatId: (userData: CreateNewUserProps) => Promise<void>;
  sendUserMessage: (
    message: string,
    chatId: string,
    callbacks: StreamMessageCallbacks
  ) => void;
  getWelcomeMessages: (chatId: string) => Promise<ChatActionResponse>;
  submitFeedback: (
    chatId: string,
    type: string,
    score: number
  ) => Promise<void>;
  submitMessageFeedback: (
    feedbackData: SubmitMessageFeedbackProps
  ) => Promise<void>;
  getChatHistory: (chatId: string) => Promise<ChatHistoryResponse>;
  restoreSession: (chatId: string) => void;
  clearChatSession: () => void;
}

type APIProviderState = {
  chatId: string;
};

export const Context = createContext<APIController>({
  apiProviderState: {
    chatId: "",
  },
  getUserChatId: async () => undefined,
  sendUserMessage: () => undefined,
  getWelcomeMessages: async () => ({
    chatId: "",
    action: "CONVERSATION_START",
    messages: [],
  }),
  submitFeedback: async () => undefined,
  submitMessageFeedback: async () => undefined,
  getChatHistory: async () => ({ messages: [], isInputDisabled: false }),
  restoreSession: () => undefined,
  clearChatSession: () => undefined,
});

Context.displayName = "APIContext";

const APIProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [apiProviderState, setApiProviderState] = useState<APIProviderState>({
    chatId: "",
  });
  const {
    createNewUser,
    chatStream,
    getChatAction,
    submitFeedback,
    submitMessageFeedback,
    getChatHistory: handleChatHistory,
  } = useApiController();

  const getUserChatId = async (userData: CreateNewUserProps): Promise<void> => {
    try {
      const response = await createNewUser(userData);

      if (response.chatId) {
        setApiProviderState((prevState) => ({
          ...prevState,
          ...response,
        }));
      }
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

  const sendUserMessage = (
    message: string,
    chatId: string,
    callbacks: StreamMessageCallbacks
  ) => {
    callbacks.onStart();

    chatStream(
      { chatId, message },
      (chunk) => callbacks.onChunk(chunk),
      () => callbacks.onDone()
    )
      .then()
      .catch((error) => {
        console.log(error);
      });
  };

  const handleSubmitFeedback = async (
    chatId: string,
    type: string,
    score: number
  ): Promise<void> => {
    return submitFeedback({ chatId, type, score });
  };

  const getWelcomeMessages = async (
    chatId: string
  ): Promise<ChatActionResponse> => {
    return getChatAction({
      chatId,
      action: ChatActionTypes.CONVERSATION_START,
    });
  };

  const restoreSession = (chatId: string) => {
    setApiProviderState((prev) => ({ ...prev, chatId }));
  };

  const clearChatSession = () => {
    setApiProviderState((prev) => ({ ...prev, chatId: "" }));
  };

  const value = useMemo(
    () => ({
      apiProviderState,
      getUserChatId,
      sendUserMessage,
      getWelcomeMessages,
      submitFeedback: handleSubmitFeedback,
      submitMessageFeedback,
      getChatHistory: handleChatHistory,
      restoreSession,
      clearChatSession,
    }),
    [apiProviderState]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export default APIProvider;
